import React, {useState, useEffect, useRef} from 'react';
import {View, ScrollView} from 'react-native';
import {connect} from 'react-redux';

import {basicStyles} from '../../../styles/basic';
import PageTop from '../../../components/PageTop';
import PageContentContainer from '../../../components/PageContentContainer';
import Avatar from '../../../components/Avatar';
import {AVATAR_DEFAULT, AVATAR_PATH_ORG} from '../../../config';
import FieldSet from '../../../components/FieldSet';
import AppText from "../../../components/AppText";
import {textStyles} from "../../../styles/text";
import IntegrationsService from "../../../services/IntegrationsService";
import OrganizationsService from "../../../services/OrganizationsService";
import {useDebouncedCallback} from "use-debounce";
import Scripts from "./components/Scripts";
import StoreFrontsService from "../../../services/StoreFrontsService";
import UsersService from "../../../services/UsersService";
import {getUserDisplayName} from "../../../utilities/methods";
import Automations from "./components/Automations";
import TaskPerMessage from "./components/TaskPerMessage";
import MessagesService from "../../../services/MessagesService";
import BulkReassignmentPicker from "../../../components/BulkReassignmentPicker";
import AppPickerInput from "../../../components/AppPickerInput";
import AppButton from "../../../components/AppButton";
import {useDimensions} from "../../../hooks/useDimensions";

let minutes = [];
for (let i = 0; i <= 60; i++) {
    let min = i + '';

    minutes.push({key: min, value: min});
}

function MessageFlowSettings(props) {
    const organizationId = props.route?.params?.organizationId || props.auth.organization.organization_id;

    const {width, isMobile} = useDimensions();

    const [organization, setOrganization] = useState({});
    const [organizationTextLineGroup, setOrganizationTextLineGroup] = useState({});
    const [storeFronts, setStoreFronts] = useState([]);
    const [organizationUsers, setOrganizationUsers] = useState([]);
    const [scrollEnabled, setScrollEnabled] = useState(true);
    const firstUpdate = useRef(true);
    const [userFeeds, setUserFeeds] = useState({});
    const [selectedFeedIds, setSelectedFeedIds] = useState([]);
    const [bulkReassignTarget, setBulkReassignTarget] = useState('');
    const [bulkReassignLoading, setBulkReassignLoading] = useState(false);

    const updateOrganization = (prop, value) => {
        setOrganization({...organization, [prop]: value});
    };

    const [updateOrganizationDebounce] = useDebouncedCallback(() => {
        if (organizationId) {
            OrganizationsService.updateOrganization(
                organizationId,
                organization,
            );
        }
    }, 500);

    useEffect(() => {
        if (organizationId) {
            OrganizationsService.getOrganization(organizationId)
                .then((organization) => {
                    setOrganization(organization);
                });

            IntegrationsService.getOrganizationTextlineGroup(organizationId)
                .then(groupData => {
                    setOrganizationTextLineGroup(groupData.group);
                });

            StoreFrontsService.getStoreFronts(organizationId)
                .then((storeFrontsData) => {
                    setStoreFronts(storeFrontsData);
                });

            UsersService.getMessageflowUsers(organizationId)
                .then((data) => {
                    setOrganizationUsers(data.users);
                });

            MessagesService.getUnresolvedFeedsByUser(organizationId)
                .then((data) => {
                    setUserFeeds(data.feedsByUser || {});
                });
        }
    }, [organizationId]);

    const reassignTargetOptions = [
        ...organizationUsers.map(user => ({
            label: getUserDisplayName(user),
            value: 'user_' + user.user_organization_id,
        })),
        ...storeFronts.map(sf => ({
            label: sf.store_front_title + ' (Group)',
            value: 'group_' + sf.store_front_id,
        })),
    ];

    const handleBulkReassign = async () => {
        if (selectedFeedIds.length === 0 || bulkReassignTarget === '') return;

        setBulkReassignLoading(true);
        try {
            let targetType, targetId;
            if (bulkReassignTarget.startsWith('user_')) {
                targetType = 'user';
                targetId = bulkReassignTarget.replace('user_', '');
            } else {
                targetType = 'group';
                targetId = bulkReassignTarget.replace('group_', '');
            }

            await MessagesService.bulkReassignFeeds(
                organizationId,
                props.auth.organization.organization_id,
                selectedFeedIds,
                targetType,
                targetId
            );

            const data = await UsersService.getMessageflowUsers(organizationId);
            setOrganizationUsers(data.users);

            const feedsData = await MessagesService.getUnresolvedFeedsByUser(organizationId);
            setUserFeeds(feedsData.feedsByUser || {});

            setSelectedFeedIds([]);
            setBulkReassignTarget('');
        } catch (e) {
            // Error handled by Http utility
        }
        setBulkReassignLoading(false);
    };

    useEffect(() => {
        if (firstUpdate.current) {
            if (Object.keys(organization).length) {
                firstUpdate.current = false;
            }
            return;
        }

        updateOrganizationDebounce();
    }, [organization, updateOrganizationDebounce]);

    return (
        <ScrollView contentContainerStyle={basicStyles.flexScale} scrollEnabled={scrollEnabled}>
            <PageTop style={[
                !isMobile ? basicStyles.flexRow : basicStyles.flexWrap,
                isMobile ? {padding: 0, paddingVertical: 45, alignItems: 'center'} : null,
                basicStyles.alignContentCenter,
                basicStyles.justifyContentSpaceBetween,
                {gap: 48},
            ]}>
                <View style={[
                    basicStyles.flexRow,
                    basicStyles.alignContentCenter,
                    basicStyles.justifyContentSpaceBetween,
                ]}>
                    <Avatar
                        source={{uri: organization.organization_thumbnail ? AVATAR_PATH_ORG + organization.organization_thumbnail : AVATAR_DEFAULT}}
                        width={66}
                        style={{marginRight: 20}}
                    />
                    <AppText style={[
                        textStyles.pageTitle,
                        textStyles.semiBold,
                    ]}>
                        Fluid Inbox Settings
                    </AppText>
                </View>

                {organization.organization_postmark_inbound_address && (
                    <View style={[
                        {flex: 1},
                        isMobile ? {paddingHorizontal: 24, maxWidth: '100%'} : null,
                    ]}>
                        <AppText style={[
                            textStyles.pageSubTitle,
                            textStyles.semiBold,
                            {marginBottom: 0},
                        ]}>
                            Forwarding Email: {organization.organization_postmark_inbound_address}
                        </AppText>
                    </View>
                )}
            </PageTop>
            <PageContentContainer>
                <AppText style={[textStyles.pageSubTitle, textStyles.semiBold]}>
                    Task Assignment
                </AppText>
                <FieldSet
                    style={{marginBottom: 50}}
                    chunkSize={isMobile ? 1 : 2}
                    fields={[
                        {
                            type: 'picker',
                            label: 'Group',
                            onValueChange: value => updateOrganization('organization_default_messageflow_store_front_id', value),
                            selectedValue: organization.organization_default_messageflow_store_front_id?.toString(),
                            items: storeFronts,
                            labelExtractor: 'store_front_title',
                            valueExtractor: (item) => item.store_front_id.toString()
                        },
                        {
                            type: 'picker',
                            label: 'User',
                            onValueChange: value => updateOrganization('organization_default_messageflow_user_organization_id', value),
                            selectedValue: organization.organization_default_messageflow_user_organization_id?.toString(),
                            items: organizationUsers,
                            labelExtractor: (item) => getUserDisplayName(item),
                            valueExtractor: (item) => item.user_id.toString()
                        }
                    ]}
                />

                <AppText style={[textStyles.pageSubTitle, textStyles.semiBold]}>
                    Bulk Reassignment
                </AppText>
                <View style={[
                    !isMobile ? basicStyles.flexRow : basicStyles.flexWrap,
                    basicStyles.alignContentCenter,
                    {gap: 15, marginBottom: 50, zIndex: 10, position: 'relative'},
                ]}>
                    <View style={[{flex: 1, zIndex: 3}, isMobile ? {width: '100%'} : null]}>
                        <BulkReassignmentPicker
                            userFeeds={userFeeds}
                            selectedFeedIds={selectedFeedIds}
                            onSelectionChange={setSelectedFeedIds}
                            label="Currently Assigned Tasks"
                            organizationUsers={organizationUsers}
                        />
                    </View>
                    <View style={[{flex: 1, zIndex: 2}, isMobile ? {width: '100%'} : null]}>
                        <AppPickerInput
                            label="Reassign Tasks To"
                            title="User"
                            selectedValue={bulkReassignTarget}
                            onValueChange={setBulkReassignTarget}
                            items={reassignTargetOptions}
                            labelExtractor="label"
                            valueExtractor="value"
                        />
                    </View>
                    <View style={[{zIndex: 1}, isMobile ? {width: '100%'} : null]}>
                        <AppButton
                            label={bulkReassignLoading ? 'Reassigning...' : 'Reassign'}
                            theme="green"
                            action={handleBulkReassign}
                            disabled={bulkReassignLoading || selectedFeedIds.length === 0 || !bulkReassignTarget}
                        />
                    </View>
                </View>

                <Automations organizationId={organizationId} setScrollEnabled={setScrollEnabled} />

                <AppText style={[textStyles.pageSubTitle, textStyles.semiBold]}>
                    Notifications
                </AppText>
                <FieldSet
                    style={{marginBottom: 50}}
                    chunkSize={ isMobile ? 1 : 2}
                    fields={[
                        {
                            type: 'toggle',
                            label: 'Notify Business Email when new inbound message has arrived',
                            value: !!parseInt(organization.organization_message_notifications_enabled),
                            onChange: (value) => {
                                const update = value ? '1' : '0';
                                updateOrganization('organization_message_notifications_enabled', update);
                            }
                        },
                        {
                            type: 'picker',
                            label: 'Time between inbound messages by the minute',
                            title: 'minutes',
                            onValueChange: value => {
                                updateOrganization('organization_message_notifications_rate_minutes', value);
                            },
                            selectedValue: organization.organization_message_notifications_rate_minutes,
                            labelExtractor: 'value',
                            valueExtractor: 'value',
                            items: minutes
                        }
                    ]}
                />

                <TaskPerMessage organizationId={organizationId} />

                <Scripts organizationId={organizationId} setScrollEnabled={setScrollEnabled}/>
            </PageContentContainer>
        </ScrollView>
    );
}

const mapStateToProps = (state) => {
    const {auth} = state;
    return {auth};
};

export default connect(mapStateToProps)(MessageFlowSettings)
